import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

// Fully-editable Success Story spotlight block. Data is provided directly by
// the CMS block config (see registry.js `success_story` schema). See
// /app/backend/seed_data/batch_e_pages.py for seeded defaults.

function resolveMediaUrl(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
}

export default function SuccessStoryBlock({ data }) {
  const d = data || {};
  const badge = d.badge || "";
  const headlineLead = d.headline_lead || "";
  const headlineHighlight = d.headline_highlight || "";
  const achievements = Array.isArray(d.achievements)
    ? d.achievements.filter((a) => a && a.text)
    : [];
  const authorName = d.author_name || "";
  const authorTitle = d.author_title || "";
  const authorAvatar = d.author_avatar || "";
  const mainImage = d.main_image || "";
  const mainImageAlt = d.main_image_alt || "";
  const statNumber = d.stat_number || "";
  const statLabel = d.stat_label || "";
  const ctaLabel = d.cta_label || "";
  const ctaHref = d.cta_href || "";

  return (
    <section
      data-testid="success-story-section"
      className="py-12 sm:py-16 lg:py-24 bg-slate-50/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-10 lg:gap-16 items-center"
        >
          {/* Left */}
          <div>
            {badge && (
              <span
                data-testid="success-story-badge"
                className="inline-block text-xs font-semibold text-sky-600 tracking-widest uppercase bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 mb-6"
              >
                {badge}
              </span>
            )}

            {(headlineLead || headlineHighlight) && (
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.6rem] font-extrabold tracking-tight text-slate-900 leading-[1.15]">
                {headlineLead}
                {headlineHighlight && (
                  <>
                    {headlineLead ? " " : ""}
                    <span className="text-sky-600 italic font-light">{headlineHighlight}</span>
                  </>
                )}
              </h2>
            )}

            {achievements.length > 0 && (
              <ul
                data-testid="success-story-achievements"
                className="mt-8 space-y-4"
              >
                {achievements.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-base lg:text-lg text-slate-700 leading-relaxed"
                  >
                    <span
                      className="mt-2.5 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0"
                      aria-hidden
                    />
                    <span>{line.text}</span>
                  </li>
                ))}
              </ul>
            )}

            {(authorName || authorTitle) && (
              <div className="mt-8 flex items-center gap-4">
                {authorAvatar && (
                  <img
                    src={resolveMediaUrl(authorAvatar)}
                    alt={authorName}
                    data-testid="success-story-avatar"
                    className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                  />
                )}
                <div>
                  {authorName && (
                    <p className="text-base font-bold text-slate-900">{authorName}</p>
                  )}
                  {authorTitle && <p className="text-sm text-slate-500">{authorTitle}</p>}
                </div>
              </div>
            )}

            {ctaLabel && (
              <div className="mt-8">
                <Link to={ctaHref || "/"}>
                  <Button
                    data-testid="success-story-cta-btn"
                    className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-7 h-11 text-sm font-semibold"
                  >
                    {ctaLabel}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="relative">
            {mainImage && (
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src={resolveMediaUrl(mainImage)}
                  alt={mainImageAlt}
                  data-testid="success-story-image"
                  className="w-full h-60 sm:h-80 lg:h-[28rem] object-cover"
                />
              </div>
            )}

            {(statNumber || statLabel) && (
              <div
                data-testid="success-story-stat-card"
                className="absolute -bottom-4 right-3 sm:-bottom-5 sm:-right-3 lg:right-6 bg-white rounded-xl shadow-xl border border-slate-200 px-4 py-3 sm:px-5 sm:py-4 text-center"
              >
                {statNumber && (
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-sky-600">
                    {statNumber}
                  </p>
                )}
                {statLabel && (
                  <p className="text-[10px] sm:text-xs font-medium text-slate-500 mt-0.5">
                    {statLabel}
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
